import { NetworkId, WalletBalance } from '../types';
import { cgGet } from './coingecko';

export interface NetworkConfig {
  id: NetworkId;
  name: string;
  symbol: string;
  coinId: string;
  color: string;
  addressPlaceholder: string;
}

export const NETWORKS: Record<NetworkId, NetworkConfig> = {
  ethereum: {
    id: 'ethereum',
    name: 'Ethereum',
    symbol: 'ETH',
    coinId: 'ethereum',
    color: '#627EEA',
    addressPlaceholder: '0x742d35Cc6634C0532925a3b8D4C9…',
  },
  bitcoin: {
    id: 'bitcoin',
    name: 'Bitcoin',
    symbol: 'BTC',
    coinId: 'bitcoin',
    color: '#F7931A',
    addressPlaceholder: 'bc1q… or 1… or 3…',
  },
  solana: {
    id: 'solana',
    name: 'Solana',
    symbol: 'SOL',
    coinId: 'solana',
    color: '#9945FF',
    addressPlaceholder: 'Base58 address (32–44 chars)',
  },
  bnb: {
    id: 'bnb',
    name: 'BNB Chain',
    symbol: 'BNB',
    coinId: 'binancecoin',
    color: '#F3BA2F',
    addressPlaceholder: '0x742d35Cc6634C0532925a3b8D4C9…',
  },
  polygon: {
    id: 'polygon',
    name: 'Polygon',
    symbol: 'MATIC',
    coinId: 'matic-network',
    color: '#8247E5',
    addressPlaceholder: '0x742d35Cc6634C0532925a3b8D4C9…',
  },
};

export const NETWORK_IDS: NetworkId[] = ['ethereum', 'bitcoin', 'solana', 'bnb', 'polygon'];

// ── Address helpers ───────────────────────────────────────────────────────────

export function shortenAddress(address: string, chars = 6): string {
  if (address.length <= chars * 2 + 3) return address;
  return `${address.slice(0, chars)}…${address.slice(-chars)}`;
}

export function validateAddress(network: NetworkId, address: string): string | null {
  const a = address.trim();
  if (!a) return 'Please enter a wallet address.';
  switch (network) {
    case 'ethereum':
    case 'bnb':
    case 'polygon':
      if (!/^0x[0-9a-fA-F]{40}$/.test(a))
        return 'Invalid address. Expected 0x followed by 40 hex characters.';
      break;
    case 'bitcoin':
      // P2PKH, P2SH, Bech32, Bech32m
      if (!/^(1[a-km-zA-HJ-NP-Z1-9]{25,34}|3[a-km-zA-HJ-NP-Z1-9]{25,34}|bc1[a-zA-HJ-NP-Za-z0-9]{39,87})$/.test(a))
        return 'Invalid Bitcoin address.';
      break;
    case 'solana':
      if (!/^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(a))
        return 'Invalid Solana address (expected 32–44 Base58 characters).';
      break;
  }
  return null;
}

// ── Price fetch ───────────────────────────────────────────────────────────────

async function fetchCoinPriceUsd(coinId: string): Promise<number> {
  try {
    const { data } = await cgGet('/simple/price', {
      params: { ids: coinId, vs_currencies: 'usd' },
      timeout: 10_000,
    });
    return ((data as any)[coinId]?.usd as number) ?? 0;
  } catch {
    return 0;
  }
}

// ── Native balance fetchers ───────────────────────────────────────────────────

async function fetchNativeBalance(network: NetworkId, address: string): Promise<number> {
  switch (network) {
    case 'ethereum': {
      const res = await fetch(
        `https://api.etherscan.io/api?module=account&action=balance&address=${address}&tag=latest`,
        { signal: AbortSignal.timeout(12_000) }
      );
      if (!res.ok) throw new Error(`Etherscan HTTP ${res.status}`);
      const data = await res.json();
      if (data.status !== '1') throw new Error(data.result ?? data.message ?? 'Etherscan error');
      return Number(data.result) / 1e18;
    }

    case 'bitcoin': {
      const res = await fetch(`https://blockstream.info/api/address/${address}`, {
        signal: AbortSignal.timeout(12_000),
      });
      if (!res.ok) throw new Error(`Blockstream HTTP ${res.status}`);
      const data = await res.json();
      const sats =
        (data.chain_stats?.funded_txo_sum ?? 0) - (data.chain_stats?.spent_txo_sum ?? 0);
      return sats / 1e8;
    }

    case 'solana': {
      const res = await fetch('https://api.mainnet-beta.solana.com', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ jsonrpc: '2.0', method: 'getBalance', params: [address], id: 1 }),
        signal: AbortSignal.timeout(12_000),
      });
      if (!res.ok) throw new Error(`Solana RPC HTTP ${res.status}`);
      const data = await res.json();
      if (data.error) throw new Error(data.error.message ?? 'Solana RPC error');
      return (data.result?.value ?? 0) / 1e9;
    }

    case 'bnb': {
      const res = await fetch(
        `https://api.bscscan.com/api?module=account&action=balance&address=${address}&tag=latest`,
        { signal: AbortSignal.timeout(12_000) }
      );
      if (!res.ok) throw new Error(`BSCScan HTTP ${res.status}`);
      const data = await res.json();
      if (data.status !== '1') throw new Error(data.result ?? data.message ?? 'BSCScan error');
      return Number(data.result) / 1e18;
    }

    case 'polygon': {
      const res = await fetch(
        `https://api.polygonscan.com/api?module=account&action=balance&address=${address}&tag=latest`,
        { signal: AbortSignal.timeout(12_000) }
      );
      if (!res.ok) throw new Error(`Polygonscan HTTP ${res.status}`);
      const data = await res.json();
      if (data.status !== '1') throw new Error(data.result ?? data.message ?? 'Polygonscan error');
      return Number(data.result) / 1e18;
    }

    default:
      throw new Error('Unsupported network');
  }
}

// ── Public API ────────────────────────────────────────────────────────────────

export async function fetchWalletBalances(
  network: NetworkId,
  address: string
): Promise<WalletBalance[]> {
  const cfg = NETWORKS[network];
  const [amount, priceUsd] = await Promise.all([
    fetchNativeBalance(network, address),
    fetchCoinPriceUsd(cfg.coinId),
  ]);

  if (amount <= 0) return [];

  return [
    {
      symbol:   cfg.symbol,
      name:     cfg.name,
      coinId:   cfg.coinId,
      amount,
      priceUsd,
      valueUsd: amount * priceUsd,
    },
  ];
}
